import { useState } from "react";
import { createPortal } from "react-dom";

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function safe(val, fallback = "—") {
  if (val === null || val === undefined || val === "undefined") return fallback;
  return val;
}
function safeNum(val, fallback = 0) {
  const n = Number(val);
  return isNaN(n) ? fallback : n;
}
function fmtSafe(val, currency) {
  const n = safeNum(val, null);
  return n === null ? "—" : fmt(n, currency);
}

function StatPill({ label, value, color = "var(--text)" }) {
  return (
    <div style={{ background: "var(--bg3)", border: "1px solid var(--border)", borderRadius: 8, padding: "12px 14px" }}>
      <div style={{ fontSize: 10, color: "var(--text3)", textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 500, marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 17, fontWeight: 700, fontFamily: "DM Mono, monospace", color }}>{safe(value, "—")}</div>
    </div>
  );
}

function SalesBar({ months, mode = "quantity", currency = "₦" }) {
  // Ensure all 12 months are present, fill missing with zeros
  const filled = Array.from({ length: 12 }, (_, i) => {
    const found = months.find((m) => m.month === i);
    return found || { month: i, quantity: 0, salesPrice: 0 };
  });

  const values = filled.map((m) => safeNum(mode === "revenue" ? m.salesPrice : m.quantity));
  const max = Math.max(...values, 1); // never divide by 0
  const hasData = values.some((v) => v > 0);

  return (
    <div>
      {!hasData && (
        <div style={{ textAlign: "center", padding: "18px 0", fontSize: 12, color: "var(--text3)" }}>
          No {mode === "revenue" ? "revenue" : "sales"} recorded for this period
        </div>
      )}
      <div style={{ display: "flex", alignItems: "flex-end", gap: 3, height: 64, paddingTop: 8 }}>
        {filled.map((m, i) => {
          const val = values[i];
          const h = val > 0 ? Math.max(4, Math.round((val / max) * 56)) : 2;
          const label = mode === "revenue" ? `${MONTH_NAMES[m.month]}: ${currency}${val.toLocaleString()}` : `${MONTH_NAMES[m.month]}: ${val} units`;
          return (
            <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
              <div
                title={label}
                style={{
                  width: "100%",
                  height: h,
                  background: val > 0 ? "var(--blue)" : "var(--border)",
                  borderRadius: "3px 3px 0 0",
                  opacity: val > 0 ? 1 : 0.4,
                  transition: "height 0.35s ease",
                  cursor: val > 0 ? "default" : "default",
                }}
              />
              <span style={{ fontSize: 8, color: "var(--text3)", fontFamily: "DM Mono, monospace" }}>{MONTH_NAMES[m.month].slice(0, 1)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function InfoRow({ label, children, value, mono }) {
  const content =
    children ||
    (mono ? <span style={{ fontFamily: "DM Mono, monospace", fontSize: 12 }}>{safe(value)}</span> : <span style={{ fontSize: 13 }}>{safe(value)}</span>);
  return (
    <div
      style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid var(--border)", gap: 16 }}
    >
      <span style={{ fontSize: 11, color: "var(--text3)", textTransform: "uppercase", letterSpacing: 0.4, fontWeight: 500, flexShrink: 0, minWidth: 110 }}>
        {label}
      </span>
      <span style={{ textAlign: "right", color: "var(--text2)" }}>{content}</span>
    </div>
  );
}

const TABS = [
  { key: "overview", label: "Overview", icon: "ti-info-circle" },
  { key: "inventory", label: "Inventory", icon: "ti-stack-2" },
  { key: "sales", label: "Sales", icon: "ti-chart-bar" },
  { key: "sub-units", label: "Sub Units", icon: "ti-layers-intersect" },
  { key: "history", label: "History", icon: "ti-history" },
];

function SalesChartCard({ allSalesOffline, currency = "₦" }) {
  // allSalesOffline is Array<{ year, salesByMonth: Array<{month,quantity,salesPrice}> }>
  const years = Array.isArray(allSalesOffline) ? allSalesOffline : [];
  const currentYear = new Date().getFullYear();

  // Default to current year entry, fall back to last entry
  const defaultEntry = years.find((e) => e.year === currentYear) || years[years.length - 1];
  const [selectedYear, setSelectedYear] = useState(defaultEntry?.year ?? currentYear);
  const [mode, setMode] = useState("quantity"); // "quantity" | "revenue"

  const entry = years.find((e) => e.year === selectedYear) || years[0];
  const rawMonths = entry?.salesByMonth || [];

  // Fill all 12 months — some entries may be missing if schema was updated mid-year
  const filled = Array.from({ length: 12 }, (_, i) => {
    const found = rawMonths.find((m) => m.month === i);
    return found || { month: i, quantity: 0, salesPrice: 0 };
  });

  const totalQty = filled.reduce((s, m) => s + safeNum(m.quantity), 0);
  const totalRev = filled.reduce((s, m) => s + safeNum(m.salesPrice), 0);
  const bestMonth = filled.reduce((best, m) => {
    const val = safeNum(mode === "revenue" ? m.salesPrice : m.quantity);
    const bestVal = safeNum(mode === "revenue" ? best.salesPrice : best.quantity);
    return val > bestVal ? m : best;
  }, filled[0]);
  const bestVal = safeNum(mode === "revenue" ? bestMonth?.salesPrice : bestMonth?.quantity);

  if (!years.length) {
    return <div style={{ textAlign: "center", padding: "32px 0", color: "var(--text3)", fontSize: 13 }}>No offline sales data available</div>;
  }

  return (
    <div style={{ background: "var(--bg3)", borderRadius: 8, border: "1px solid var(--border)", marginBottom: 16, overflow: "hidden" }}>
      {/* ── Top bar: year picker + mode toggle ── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "12px 14px",
          borderBottom: "1px solid var(--border)",
          flexWrap: "wrap",
          gap: 8,
        }}
      >
        {/* Year tabs */}
        <div style={{ display: "flex", gap: 4 }}>
          {years.map((e) => (
            <button
              key={e.year}
              onClick={() => setSelectedYear(e.year)}
              style={{
                padding: "4px 12px",
                borderRadius: 5,
                border: "1px solid",
                cursor: "pointer",
                fontSize: 12,
                fontFamily: "DM Mono, monospace",
                fontWeight: 600,
                background: selectedYear === e.year ? "var(--blue)" : "var(--bg4)",
                borderColor: selectedYear === e.year ? "var(--blue)" : "var(--border)",
                color: selectedYear === e.year ? "#fff" : "var(--text3)",
              }}
            >
              {e.year}
            </button>
          ))}
        </div>

        {/* Units / Revenue toggle */}
        <div style={{ display: "flex", background: "var(--bg4)", border: "1px solid var(--border)", borderRadius: 6, padding: 2, gap: 2 }}>
          {[
            { key: "quantity", label: "Units" },
            { key: "revenue", label: "Revenue" },
          ].map((opt) => (
            <button
              key={opt.key}
              onClick={() => setMode(opt.key)}
              style={{
                padding: "3px 10px",
                borderRadius: 4,
                cursor: "pointer",
                fontSize: 11,
                fontFamily: "inherit",
                fontWeight: mode === opt.key ? 600 : 400,
                background: mode === opt.key ? "var(--bg2)" : "transparent",
                color: mode === opt.key ? "var(--text)" : "var(--text3)",
                border: mode === opt.key ? "1px solid var(--border)" : "1px solid transparent",
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Summary row ── */}
      <div style={{ display: "flex", gap: 20, padding: "10px 14px 0" }}>
        <div>
          <div style={{ fontSize: 9, color: "var(--text3)", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 2 }}>
            {mode === "quantity" ? "Total Units Sold" : "Total Revenue"}
          </div>
          <div style={{ fontSize: 18, fontWeight: 700, fontFamily: "DM Mono, monospace", color: "var(--blue)" }}>
            {mode === "quantity" ? totalQty.toLocaleString() : `${currency}${totalRev.toLocaleString()}`}
          </div>
        </div>
        {bestVal > 0 && (
          <div>
            <div style={{ fontSize: 9, color: "var(--text3)", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 2 }}>Best Month</div>
            <div style={{ fontSize: 18, fontWeight: 700, fontFamily: "DM Mono, monospace", color: "var(--teal)" }}>
              {MONTH_NAMES[bestMonth.month]}
              <span style={{ fontSize: 12, fontWeight: 400, color: "var(--text3)", marginLeft: 6 }}>
                {mode === "quantity" ? `${bestVal} units` : `${currency}${bestVal.toLocaleString()}`}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* ── Bar chart ── */}
      <div style={{ padding: "4px 14px 0" }}>
        <SalesBar months={filled} mode={mode} currency={currency} />
      </div>

      {/* ── Month breakdown grid ── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", borderTop: "1px solid var(--border)", marginTop: 4 }}>
        {filled.map((m) => {
          const val = safeNum(mode === "revenue" ? m.salesPrice : m.quantity);
          const hasVal = val > 0;
          return (
            <div
              key={m.month}
              style={{
                padding: "7px 10px",
                borderRight: "1px solid var(--border)",
                borderBottom: "1px solid var(--border)",
                background: hasVal ? "transparent" : "var(--bg3)",
              }}
            >
              <div style={{ fontSize: 10, color: "var(--text3)", marginBottom: 2 }}>{MONTH_NAMES[m.month]}</div>
              <div
                style={{
                  fontSize: 12,
                  fontFamily: "DM Mono, monospace",
                  fontWeight: hasVal ? 600 : 400,
                  color: hasVal ? "var(--text)" : "var(--text3)",
                }}
              >
                {mode === "revenue" ? (val > 0 ? `${currency}${val.toLocaleString()}` : "—") : val > 0 ? val.toLocaleString() : "—"}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function ProductModal({ product: p, onClose, currency = "₦" }) {
  const [tab, setTab] = useState("overview");

  const lowStock = safeNum(p.quantity) <= safeNum(p.stockLimit);
  const unitPrice = safeNum(p.unitPrice);
  const costPrice = safeNum(p.costPrice);
  const margin = unitPrice && costPrice ? Math.round(((unitPrice - costPrice) / unitPrice) * 100) : 0;
  // allSalesOffline is handled inside SalesChartCard per-year
  const totalOffline = (p.allSalesOffline || []).reduce((sum, yr) => sum + (yr.salesByMonth || []).reduce((s, m) => s + safeNum(m.quantity), 0), 0);
  const displayPrice = p.onSales ? safeNum(p.discountPrice) : unitPrice;

  const modal = (
    <div
      onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(0,0,0,0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "6px",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--bg2)",
          border: "1px solid var(--border)",
          borderRadius: 12,
          width: "100%",
          maxWidth: 780,
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: "0 24px 64px rgba(0,0,0,0.35)",
        }}
      >
        {/* ── Modal header ── */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "14px 20px",
            borderBottom: "1px solid var(--border)",
            flexShrink: 0,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, fontWeight: 600, color: "var(--text)" }}>
            <i className='ti ti-package' style={{ color: "var(--blue)", fontSize: 16 }} />
            Product Details
          </div>
          <button
            onClick={onClose}
            style={{
              width: 30,
              height: 30,
              borderRadius: 6,
              border: "1px solid var(--border)",
              background: "var(--bg3)",
              color: "var(--text3)",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 15,
            }}
          >
            <i className='ti ti-x' />
          </button>
        </div>

        {/* ── Hero strip ── */}
        <div style={{}} className='product-hero-strip_new'>
          {/* Icon / image */}
          <div
            style={{
              width: 68,
              height: 68,
              borderRadius: 10,
              background: "var(--bg4)",
              border: "1px solid var(--border)",
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {p.productImage?.length > 0 ? (
              <img
                style={{
                  width: "90%",
                  borderRadius: 8,
                }}
                src={`${p.productImage[0]}`}
              />
            ) : (
              <i className='ti ti-package' style={{ fontSize: 26, color: "var(--amber)", opacity: 0.7 }} />
            )}
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: "var(--text)", marginBottom: 3, lineHeight: 1.3 }}>{safe(p.name)}</div>
            <div style={{ fontSize: 12, color: "var(--text3)", marginBottom: 8, display: "flex", gap: 10, flexWrap: "wrap" }}>
              {p.brand && (
                <span>
                  <i className='ti ti-building-factory-2' style={{ fontSize: 10 }} /> {p.brand}
                </span>
              )}
              {p.storeName && (
                <span>
                  <i className='ti ti-building-store' style={{ fontSize: 10 }} /> {p.storeName}
                </span>
              )}
              {p.scannedID && (
                <span style={{ fontFamily: "DM Mono, monospace" }}>
                  <i className='ti ti-barcode' style={{ fontSize: 10 }} /> {p.scannedID}
                </span>
              )}
            </div>
            <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
              {p.productCategory && <span className='chip'>{p.productCategory}</span>}
              {p.unit && <span className='chip'>{p.unit}</span>}
              {p.productState && (
                <span className='chip' style={{ textTransform: "capitalize" }}>
                  {p.productState}
                </span>
              )}
              {p.onSales && (
                <span className='badge badge-active'>
                  <i className='ti ti-tag' />
                  On Sale
                </span>
              )}
              {lowStock && (
                <span className='badge badge-warn'>
                  <i className='ti ti-alert-triangle' />
                  Low Stock
                </span>
              )}
              {p.hasSubUnits && (
                <span className='badge badge-info'>
                  <i className='ti ti-layers-intersect' />
                  Sub-units
                </span>
              )}
              {p.canBeDelivered && (
                <span className='badge badge-purple'>
                  <i className='ti ti-truck' />
                  Deliverable
                </span>
              )}
              {p.damagedAmount > 0 && (
                <span className='badge badge-cancelled'>
                  <i className='ti ti-alert-circle' />
                  {p.damagedAmount} dmg
                </span>
              )}
            </div>
          </div>

          {/* Price block */}
          <div style={{ textAlign: "right", flexShrink: 0 }}>
            <div style={{ fontSize: 10, color: "var(--text3)", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 3 }}>Selling Price</div>
            <div style={{ fontSize: 22, fontWeight: 700, fontFamily: "DM Mono, monospace", color: p.onSales ? "var(--amber)" : "var(--teal)" }}>
              {fmtSafe(displayPrice, currency)}
            </div>
            {p.onSales && (
              <div style={{ fontSize: 12, color: "var(--text3)", textDecoration: "line-through", marginTop: 1 }}>{fmtSafe(unitPrice, currency)}</div>
            )}
            <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 4 }}>
              Cost: {fmtSafe(costPrice, currency)} · Margin: <span style={{ color: margin > 20 ? "var(--teal)" : "var(--amber)" }}>{margin}%</span>
            </div>
          </div>
        </div>

        {/* ── Tabs ── */}
        <div
          style={{ display: "flex", borderBottom: "1px solid var(--border)", background: "var(--bg2)", padding: "0 20px", flexShrink: 0, overflowX: "auto" }}
        >
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 5,
                padding: "10px 14px",
                fontSize: 12,
                fontWeight: tab === t.key ? 600 : 400,
                color: tab === t.key ? "var(--blue)" : "var(--text3)",
                borderBottom: tab === t.key ? "2px solid var(--blue)" : "2px solid transparent",
                background: "none",
                border: "none",
                borderBottomWidth: 2,
                borderBottomStyle: "solid",
                borderBottomColor: tab === t.key ? "var(--blue)" : "transparent",
                cursor: "pointer",
                whiteSpace: "nowrap",
                fontFamily: "inherit",
                transition: "color 0.13s",
              }}
            >
              <i className={`ti ${t.icon}`} style={{ fontSize: 13 }} />
              {t.label}
            </button>
          ))}
        </div>

        {/* ── Tab body (scrollable) ── */}
        <div style={{ flex: 1, overflowY: "auto", padding: "18px 20px" }}>
          {/* OVERVIEW */}
          {tab === "overview" && (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10, marginBottom: 18 }}>
                <StatPill label='Stock Qty' value={safeNum(p.quantity)} color={lowStock ? "var(--red)" : "var(--text)"} />
                <StatPill label='Total Worth' value={fmtSafe(p.totalWorth, currency)} color='var(--teal)' />
                <StatPill label='In Warehouse' value={safeNum(p.amountInWarehouse, "—")} color='var(--blue)' />
                <StatPill label='Sold Today' value={safeNum(p.soldAmountToday, 0)} color='var(--amber)' />
                <StatPill label='Sold All-time' value={safeNum(p.soldAmountAllTime, 0)} color='var(--teal)' />
                <StatPill label='Rating' value={p.rating ? `${p.rating} ★ (${safeNum(p.ratingCount)})` : "No ratings"} color='var(--amber)' />
              </div>

              {p.productDescription && (
                <div
                  style={{
                    background: "var(--bg3)",
                    borderRadius: 8,
                    padding: "12px 14px",
                    border: "1px solid var(--border)",
                    marginBottom: 16,
                    fontSize: 13,
                    color: "var(--text2)",
                    lineHeight: 1.6,
                  }}
                >
                  {p.productDescription}
                </div>
              )}

              <div
                style={{
                  fontSize: 11,
                  color: "var(--text3)",
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  fontWeight: 500,
                  marginBottom: 8,
                  marginTop: 4,
                  paddingBottom: 6,
                  borderBottom: "1px solid var(--border)",
                }}
              >
                Product Details
              </div>
              {[
                ["Category", p.productCategory],
                ["Brand", p.brand],
                ["Condition", p.productState],
                ["Base Unit", p.unit],
                ["Stock Limit", p.stockLimit != null ? `${p.stockLimit} units (reorder point)` : null],
                ["Barcode / ID", p.scannedID],
                ["Created", p.createdAt],
                [
                  "Expiry Date",
                  p.productExpiryDate ? new Date(p.productExpiryDate).toLocaleDateString("en-NG", { day: "2-digit", month: "short", year: "numeric" }) : null,
                ],
                ["Warehouse", p.warehouse?.name],
                ["Created In", p.createdInStore ? "Store" : p.createdInWarehouse ? "Warehouse" : null],
                ["Store", p.storeName],
              ]
                .filter(([, v]) => v != null && v !== "" && v !== "undefined")
                .map(([k, v]) => (
                  <InfoRow key={k} label={k} value={v} />
                ))}

              {p.canBeDelivered && (
                <>
                  <div
                    style={{
                      fontSize: 11,
                      color: "var(--text3)",
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      fontWeight: 500,
                      margin: "16px 0 8px",
                      paddingBottom: 6,
                      borderBottom: "1px solid var(--border)",
                    }}
                  >
                    Delivery
                  </div>
                  <InfoRow label='Delivery Fee' value={fmtSafe(p.deliveryFee, currency)} />
                  <InfoRow label='Delivery Time' value={p.deliveryTime ? `${p.deliveryTime} day${p.deliveryTime !== 1 ? "s" : ""}` : "—"} />
                </>
              )}

              {(p.colorApplicability || p.sizeApplicability) && (
                <>
                  <div
                    style={{
                      fontSize: 11,
                      color: "var(--text3)",
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      fontWeight: 500,
                      margin: "16px 0 8px",
                      paddingBottom: 6,
                      borderBottom: "1px solid var(--border)",
                    }}
                  >
                    Variants
                  </div>
                  {p.colorApplicability && p.selectedColors?.length > 0 && (
                    <InfoRow label='Colors'>
                      <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                        {p.selectedColors.map((c) => (
                          <span
                            key={c}
                            style={{ padding: "2px 8px", borderRadius: 4, background: "var(--bg3)", border: "1px solid var(--border)", fontSize: 11 }}
                          >
                            {c}
                          </span>
                        ))}
                      </div>
                    </InfoRow>
                  )}
                  {p.sizeApplicability && p.selectedSizes?.length > 0 && (
                    <InfoRow label='Sizes'>
                      <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                        {p.selectedSizes.map((s) => (
                          <span
                            key={s}
                            style={{
                              padding: "2px 8px",
                              borderRadius: 4,
                              background: "var(--bg3)",
                              border: "1px solid var(--border)",
                              fontSize: 11,
                              fontFamily: "DM Mono, monospace",
                            }}
                          >
                            {s}
                          </span>
                        ))}
                      </div>
                    </InfoRow>
                  )}
                </>
              )}

              {p.onSales && (
                <>
                  <div
                    style={{
                      fontSize: 11,
                      color: "var(--text3)",
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      fontWeight: 500,
                      margin: "16px 0 8px",
                      paddingBottom: 6,
                      borderBottom: "1px solid var(--border)",
                    }}
                  >
                    Active Sale
                  </div>
                  <InfoRow label='Sale Price' value={fmtSafe(p.discountPrice, currency)} />
                  <InfoRow label='Original Price' value={fmtSafe(p.unitPrice, currency)} />
                  <InfoRow label='Duration' value={p.salesDuration ? `${p.salesDuration} days` : "—"} />
                </>
              )}
            </>
          )}

          {/* INVENTORY */}
          {tab === "inventory" && (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10, marginBottom: 18 }}>
                <StatPill label='In Store' value={safeNum(p.quantity)} color={lowStock ? "var(--red)" : "var(--text)"} />
                <StatPill label='In Warehouse' value={safeNum(p.amountInWarehouse)} color='var(--blue)' />
                <StatPill label='Damaged' value={safeNum(p.damagedAmount)} color={safeNum(p.damagedAmount) > 0 ? "var(--red)" : "var(--text3)"} />
              </div>

              {/* Stock bar */}
              <div style={{ marginBottom: 18 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 6 }}>
                  <span style={{ color: "var(--text3)" }}>Stock level</span>
                  <span style={{ color: lowStock ? "var(--red)" : "var(--teal)", fontFamily: "DM Mono, monospace" }}>
                    {safeNum(p.quantity)} / {Math.max(safeNum(p.quantity), safeNum(p.stockLimit) * 3)} units
                  </span>
                </div>
                <div style={{ height: 8, background: "var(--bg4)", borderRadius: 4, overflow: "hidden" }}>
                  <div
                    style={{
                      height: "100%",
                      borderRadius: 4,
                      width: `${Math.min(100, Math.round((safeNum(p.quantity) / Math.max(safeNum(p.quantity), safeNum(p.stockLimit) * 3)) * 100))}%`,
                      background: lowStock ? "var(--red)" : safeNum(p.quantity) < safeNum(p.stockLimit) * 2 ? "var(--amber)" : "var(--teal)",
                      transition: "width 0.4s",
                    }}
                  />
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, marginTop: 4, color: "var(--text3)" }}>
                  <span>Reorder at {safeNum(p.stockLimit)} units</span>
                  {lowStock && <span style={{ color: "var(--red)", fontWeight: 600 }}>⚠ Below reorder point</span>}
                </div>
              </div>

              <InfoRow label='Cost Price' value={fmtSafe(p.costPrice, currency)} />
              <InfoRow label='Selling Price' value={fmtSafe(p.unitPrice, currency)} />
              <InfoRow label='Total Stock Worth' value={fmtSafe(p.totalWorth, currency)} />
              <InfoRow label='Margin' value={`${margin}%`} />
              <InfoRow label='Source' value={p.movedFromWarehouse ? `Moved from ${p.warehouse?.name || "Warehouse"}` : "Created in store"} />

              {safeNum(p.damagedAmount) > 0 && p.reasonForDamage?.length > 0 && (
                <>
                  <div
                    style={{
                      fontSize: 11,
                      color: "var(--text3)",
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      fontWeight: 500,
                      margin: "16px 0 8px",
                      paddingBottom: 6,
                      borderBottom: "1px solid var(--border)",
                    }}
                  >
                    Damage Report
                  </div>
                  {p.reasonForDamage.map((r, i) => (
                    <div
                      key={i}
                      style={{ background: "var(--red-bg)", border: "1px solid var(--red-dim)", borderRadius: 8, padding: "10px 13px", marginBottom: 8 }}
                    >
                      <div style={{ fontSize: 11, color: "var(--text3)", fontFamily: "DM Mono, monospace", marginBottom: 4 }}>
                        {r.date ? new Date(r.date).toLocaleDateString("en-NG", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
                      </div>
                      <div style={{ fontSize: 13, color: "var(--red)" }}>
                        <i className='ti ti-alert-circle' style={{ marginRight: 6 }} />
                        {r.reason}
                      </div>
                    </div>
                  ))}
                </>
              )}
            </>
          )}

          {/* SALES */}
          {tab === "sales" && (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10, marginBottom: 18 }}>
                <StatPill label='Sold Today' value={safeNum(p.soldAmountToday, 0)} color='var(--amber)' />
                <StatPill label='Offline (yr)' value={totalOffline} color='var(--blue)' />
                <StatPill label='Online Sales' value={p.allSalesOnline?.length || 0} color='var(--purple)' />
              </div>

              {p.allSalesOffline?.length > 0 && (
                <>
                  <div
                    style={{
                      fontSize: 11,
                      color: "var(--text3)",
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      fontWeight: 500,
                      marginBottom: 8,
                      paddingBottom: 6,
                      borderBottom: "1px solid var(--border)",
                    }}
                  >
                    Monthly Sales Volume (Offline)
                  </div>
                  <SalesChartCard allSalesOffline={p.allSalesOffline} currency={currency} />
                </>
              )}

              {p.allSalesOnline?.length > 0 && (
                <>
                  <div
                    style={{
                      fontSize: 11,
                      color: "var(--text3)",
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      fontWeight: 500,
                      marginBottom: 8,
                      paddingBottom: 6,
                      borderBottom: "1px solid var(--border)",
                    }}
                  >
                    Online Sales
                  </div>
                  <div style={{ border: "1px solid var(--border)", borderRadius: 8, overflow: "hidden" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                      <thead>
                        <tr style={{ background: "var(--bg3)" }}>
                          {["Date", "Qty", "Price", "Revenue"].map((h) => (
                            <th
                              key={h}
                              style={{
                                padding: "8px 12px",
                                textAlign: h === "Date" ? "left" : "right",
                                fontSize: 10,
                                color: "var(--text3)",
                                fontWeight: 600,
                                textTransform: "uppercase",
                              }}
                            >
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {p.allSalesOnline.map((s, i) => (
                          <tr key={i} style={{ borderTop: "1px solid var(--border)" }}>
                            <td style={{ padding: "8px 12px", color: "var(--text3)", fontFamily: "DM Mono, monospace" }}>
                              {s.date ? new Date(s.date).toLocaleDateString("en-NG", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
                            </td>
                            <td style={{ padding: "8px 12px", textAlign: "right", fontFamily: "DM Mono, monospace" }}>×{safeNum(s.quantity)}</td>
                            <td style={{ padding: "8px 12px", textAlign: "right", fontFamily: "DM Mono, monospace", color: "var(--text2)" }}>
                              {currency}
                              {safeNum(s.salesPrice).toLocaleString()}
                            </td>
                            <td style={{ padding: "8px 12px", textAlign: "right", fontFamily: "DM Mono, monospace", color: "var(--teal)", fontWeight: 600 }}>
                              {currency}
                              {(safeNum(s.salesPrice) * safeNum(s.quantity)).toLocaleString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {!p.allSalesOffline?.length && !p.allSalesOnline?.length && (
                <div style={{ textAlign: "center", padding: "44px 20px", color: "var(--text3)" }}>
                  <i className='ti ti-chart-bar' style={{ fontSize: 40, display: "block", marginBottom: 12, opacity: 0.25 }} />
                  <p style={{ fontSize: 13 }}>No sales data recorded yet</p>
                </div>
              )}
            </>
          )}

          {/* SUB-UNITS */}
          {tab === "sub-units" &&
            (!p.hasSubUnits || !p.subUnits?.length ? (
              <div style={{ textAlign: "center", padding: "44px 20px", color: "var(--text3)" }}>
                <i className='ti ti-layers-intersect' style={{ fontSize: 40, display: "block", marginBottom: 12, opacity: 0.25 }} />
                <p style={{ fontSize: 13 }}>No sub-units configured for this product</p>
              </div>
            ) : (
              <>
                <div
                  style={{
                    background: "var(--blue-bg)",
                    border: "1px solid var(--blue-dim)",
                    borderRadius: 8,
                    padding: "10px 14px",
                    marginBottom: 16,
                    fontSize: 13,
                    color: "var(--blue)",
                  }}
                >
                  <i className='ti ti-info-circle' style={{ marginRight: 6 }} />
                  Base unit: <strong>{p.unit}</strong> · {p.subUnits.length} sub-unit type{p.subUnits.length !== 1 ? "s" : ""} configured
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {p.subUnits.map((su) => (
                    <div key={su.id} style={{ background: "var(--bg3)", border: "1px solid var(--border)", borderRadius: 9, padding: "14px 16px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
                        <div>
                          <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text)" }}>{safe(su.name)}</div>
                          <div style={{ fontSize: 12, color: "var(--text3)", marginTop: 2 }}>
                            1 {su.name} = {su.conversionFactor} {p.unit}
                          </div>
                        </div>
                        <span className='badge badge-info' style={{ fontSize: 10 }}>
                          ×{su.conversionFactor}
                        </span>
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                        {[
                          { label: "In Stock", value: safeNum(su.quantityOfSubUnitInStock), color: "var(--text)" },
                          { label: "Selling Price", value: `${currency}${safeNum(su.sellingPrice).toLocaleString()}`, color: "var(--teal)" },
                          { label: "Cost Price", value: ` ${currency}${safeNum(su.costPrice).toLocaleString()}`, color: "var(--text3)" },
                        ].map((item) => (
                          <div key={item.label} style={{ background: "var(--bg4)", borderRadius: 6, padding: "9px 11px", border: "1px solid var(--border)" }}>
                            <div style={{ fontSize: 10, color: "var(--text3)", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4 }}>
                              {item.label}
                            </div>
                            <div style={{ fontSize: 14, fontWeight: 600, fontFamily: "DM Mono, monospace", color: item.color }}>{item.value}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ))}

          {/* HISTORY */}
          {tab === "history" &&
            (!p.history?.length ? (
              <div style={{ textAlign: "center", padding: "44px 20px", color: "var(--text3)" }}>
                <i className='ti ti-history' style={{ fontSize: 40, display: "block", marginBottom: 12, opacity: 0.25 }} />
                <p style={{ fontSize: 13 }}>No activity history recorded</p>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {p.history.map((h, i) => (
                  <div key={i} style={{ display: "flex", gap: 12, alignItems: "flex-start", paddingBottom: 12 }}>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0, flexShrink: 0 }}>
                      <div style={{ width: 9, height: 9, borderRadius: "50%", background: "var(--blue)", marginTop: 3 }} />
                      {i < p.history.length - 1 && <div style={{ width: 1, flex: 1, background: "var(--border)", minHeight: 20 }} />}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 11, color: "var(--text3)", fontFamily: "DM Mono, monospace", marginBottom: 5 }}>
                        {h.date ? new Date(h.date).toLocaleDateString("en-NG", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
                      </div>
                      <div style={{ background: "var(--bg3)", border: "1px solid var(--border)", borderRadius: 8, padding: "10px 12px" }}>
                        {(h.activity || []).map((a, j) => (
                          <div
                            key={j}
                            style={{
                              fontSize: 13,
                              color: "var(--text)",
                              display: "flex",
                              alignItems: "center",
                              gap: 7,
                              marginBottom: j < h.activity.length - 1 ? 5 : 0,
                            }}
                          >
                            <i className='ti ti-point-filled' style={{ fontSize: 7, color: "var(--blue)", flexShrink: 0 }} />
                            {a}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ))}
        </div>

        {/* ── Footer ── */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 8,
            padding: "12px 20px",
            borderTop: "1px solid var(--border)",
            flexShrink: 0,
            background: "var(--bg2)",
          }}
        >
          <span style={{ marginRight: "auto", fontSize: 11, color: "var(--text3)", fontFamily: "DM Mono, monospace" }}>ID: {p._id}</span>
          {/*  <button className='btn btn-sm'>
            <i className='ti ti-printer' />
            Print Label
          </button> */}
          <button className='btn' onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
  return createPortal(modal, document.body);
}

export function fmt(n, currency) {
  if (n == null) return currency + "0";
  return n >= 1000000 ? `${currency}${(n / 1000000).toFixed(1)}M` : n >= 1000 ? `${currency}${(n / 1000).toFixed(0)}K` : `${currency}${n}`;
}
